import React, { useEffect, useMemo, useState } from "react";
import { api, uploadFile, assetUrl, type Asset } from "../api";
import { toast } from "../net";
import { confirm, prompt, fmtSize } from "../util";

let cache: Asset[] | null = null;
const listeners = new Set<(a: Asset[]) => void>();
export async function loadAssets(force = false): Promise<Asset[]> {
  if (cache && !force) return cache;
  const r = await api<{ assets: Asset[] }>("/api/assets");
  cache = r.assets;
  listeners.forEach(f => f(cache!));
  return cache;
}
export function useAssets(): Asset[] {
  const [a, setA] = useState<Asset[]>(cache || []);
  useEffect(() => {
    listeners.add(setA);
    loadAssets().then(setA).catch(() => {});
    const h = () => loadAssets(true);
    window.addEventListener("hearth:assets", h);
    return () => { listeners.delete(setA); window.removeEventListener("hearth:assets", h); };
  }, []);
  return a;
}

export async function uploadMany(files: FileList | File[], folder = "") {
  let ok = 0;
  for (const f of Array.from(files)) {
    try { await uploadFile(f, folder); ok++; }
    catch (e: any) { toast(`${f.name}: ${e.message}`); }
  }
  if (ok) { toast(`Uploaded ${ok} file${ok > 1 ? "s" : ""}`); await loadAssets(true); }
}

export function ArtPanel({ kind = "image" }: { kind?: "image" | "audio" }) {
  const assets = useAssets();
  const [q, setQ] = useState("");
  const [folder, setFolder] = useState("");
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const mine = assets.filter(a => a.kind === kind);
  const folders = useMemo(() => [...new Set(mine.map(a => a.folder).filter(Boolean))].sort(), [mine]);
  const shown = mine.filter(a => (!folder || a.folder === folder) && (!q || a.name.toLowerCase().includes(q.toLowerCase())));
  const total = assets.reduce((s, a) => s + a.size, 0);

  const doUpload = async (files: FileList | File[]) => {
    setBusy(true);
    await uploadMany(files, folder);
    setBusy(false);
  };

  return (
    <div
      className={`art${over ? " over" : ""}`}
      onDragOver={e => { if (e.dataTransfer.types.includes("Files")) { e.preventDefault(); setOver(true); } }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); if (e.dataTransfer.files.length) doUpload(e.dataTransfer.files); }}
    >
      <div className="art-bar">
        <label className="btn primary">
          {busy ? "Uploading…" : "⬆ Upload"}
          <input type="file" multiple hidden accept={kind === "image" ? "image/*" : "audio/*"} onChange={e => e.target.files && doUpload(e.target.files)} />
        </label>
        <input placeholder="Search" value={q} onChange={e => setQ(e.target.value)} />
        <select value={folder} onChange={e => setFolder(e.target.value)}>
          <option value="">All folders</option>
          {folders.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
      </div>
      <div className="dim small pad-x">
        {kind === "image" ? "Drag images onto the map (GM). Maps go on the Map layer; switch layer first." : "Audio files appear here and in the Jukebox."} Library: {fmtSize(total)} used.
      </div>
      <div className={kind === "image" ? "art-grid" : "art-list"}>
        {shown.map(a => (
          <div
            key={a.id}
            className="art-item"
            draggable
            onDragStart={e => {
              e.dataTransfer.setData("application/x-hearth-asset", JSON.stringify({ url: assetUrl(a), name: a.name }));
              e.dataTransfer.setData("text/plain", location.origin + assetUrl(a));
            }}
            title={`${a.name} · ${fmtSize(a.size)}`}
          >
            {kind === "image" ? <img src={assetUrl(a)} alt={a.name} loading="lazy" /> : <span className="grow">🎵 {a.name}</span>}
            <div className="art-name">{a.name}</div>
            <div className="art-actions">
              <button className="mini" title="Rename" onClick={async () => { const n = await prompt("Rename", a.name); if (n) { await api(`/api/assets/${a.id}`, { method: "PATCH", body: { name: n } }); loadAssets(true); } }}>✎</button>
              <button className="mini" title="Move to folder" onClick={async () => { const n = await prompt("Folder", a.folder); if (n !== null) { await api(`/api/assets/${a.id}`, { method: "PATCH", body: { folder: n } }); loadAssets(true); } }}>📁</button>
              <button className="mini" title="Copy link" onClick={() => { navigator.clipboard?.writeText(location.origin + assetUrl(a)); toast("Link copied"); }}>🔗</button>
              <button className="mini danger" title="Delete" onClick={async () => { if (await confirm(`Delete ${a.name}? Tokens using it will show blank.`)) { await api(`/api/assets/${a.id}`, { method: "DELETE" }); loadAssets(true); } }}>✕</button>
            </div>
          </div>
        ))}
        {!shown.length && <div className="dim pad">{mine.length ? "Nothing matches." : kind === "image" ? "Upload maps, tokens and handout art — or drop files here." : "Upload music and ambience files."}</div>}
      </div>
    </div>
  );
}

/** Button that opens a picker over the user's image library (or takes a URL / upload). */
export function AssetPicker({ label, onPick, kind = "image" }: { label: string; onPick: (url: string, name: string) => void; kind?: "image" | "audio" }) {
  const [open, setOpen] = useState(false);
  const assets = useAssets();
  const [q, setQ] = useState("");
  const list = assets.filter(a => a.kind === kind && (!q || a.name.toLowerCase().includes(q.toLowerCase())));
  return (
    <>
      <button onClick={() => setOpen(true)}>{label}</button>
      {open && (
        <div className="modal-bg" onMouseDown={e => e.target === e.currentTarget && setOpen(false)}>
          <div className="modal wide">
            <div className="modal-title">{label}</div>
            <div className="row gap">
              <input placeholder="Search library" value={q} onChange={e => setQ(e.target.value)} autoFocus />
              <label className="btn">
                Upload…
                <input type="file" hidden accept={kind === "image" ? "image/*" : "audio/*"} onChange={async e => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  try {
                    const a = await uploadFile(f);
                    loadAssets(true);
                    onPick(a.url, a.name);
                    setOpen(false);
                  } catch (err: any) { toast(err.message); }
                }} />
              </label>
              <button onClick={async () => { const u = await prompt("Image URL (https://…)"); if (u && /^https?:\/\//.test(u)) { onPick(u, ""); setOpen(false); } }}>From URL…</button>
            </div>
            <div className={kind === "image" ? "art-grid picker" : "art-list picker"}>
              {list.map(a => (
                <button key={a.id} className="art-item" onClick={() => { onPick(assetUrl(a), a.name); setOpen(false); }} title={a.name}>
                  {kind === "image" ? <img src={assetUrl(a)} alt="" loading="lazy" /> : <span>🎵 {a.name}</span>}
                </button>
              ))}
              {!list.length && <div className="dim pad">Your library is empty — upload something.</div>}
            </div>
            <div className="modal-btns"><button onClick={() => setOpen(false)}>Close</button></div>
          </div>
        </div>
      )}
    </>
  );
}
